/**
 * ReceiptDialog — the slip that goes across the counter.
 *
 * Numbered per assembly by the server at the moment the payment is taken, so
 * two secretaries receipting at once cannot be handed the same number. A
 * reversed payment keeps its receipt and is stamped reversed rather than
 * disappearing: the giver may still be holding the original.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Printer } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import api from '@/lib/api';

const money = (n, ccy) => `${ccy} ${Number(n || 0).toLocaleString(undefined,
  { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Anything a secretary typed is untrusted. The print window is same-origin, so
 *  an unescaped donor name would run with the admin's own session. */
const esc = (value) => String(value ?? '').replace(/[&<>"']/g, (ch) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[ch]));

const printReceipt = (r) => {
  const win = window.open('', '_blank', 'width=560,height=720');
  if (!win) return;
  const line = (label, value) => (value
    ? `<tr><td style="padding:3px 10px 3px 0;color:#64748b">${esc(label)}</td><td style="padding:3px 0">${esc(value)}</td></tr>`
    : '');
  win.document.write(`<!doctype html><html><head><title>Receipt ${esc(r.receipt_number)}</title>
    <style>
      body{font-family:Georgia,serif;color:#0f172a;margin:32px;max-width:520px}
      h1{font-size:18px;margin:0}
      .muted{color:#64748b;font-size:12px}
      .no{font-family:ui-monospace,monospace;font-size:20px;letter-spacing:.06em;margin:14px 0}
      .amt{font-size:26px;font-weight:700;margin:12px 0}
      .void{border:2px solid #be123c;color:#be123c;padding:4px 10px;display:inline-block;
            transform:rotate(-4deg);font-weight:700;letter-spacing:.1em}
      hr{border:0;border-top:1px solid #e2e8f0;margin:18px 0}
      table{font-size:13px;border-collapse:collapse}
      .logo{max-height:64px;max-width:220px;margin-bottom:10px}
      @media print{button{display:none}}
    </style></head><body>
    ${r.assembly.logo_url
      ? `<img class="logo" src="${esc(r.assembly.logo_url)}" alt="${esc(r.assembly.name)}"/>`
      : ''}
    <h1>${esc(r.assembly.name)}</h1>
    <p class="muted">${esc(r.assembly.address || '')}${r.assembly.phone ? ` · ${esc(r.assembly.phone)}` : ''}</p>
    ${r.assembly.charity_no ? `<p class="muted">Registered charity ${esc(r.assembly.charity_no)}</p>` : ''}
    <hr/>
    ${r.reversed ? `<p><span class="void">REVERSED</span> ${esc(r.reversal_reason || '')}</p>` : ''}
    <p class="muted">Receipt number</p>
    <p class="no">${esc(r.receipt_number || '—')}</p>
    <p class="amt">${esc(money(r.amount, r.currency))}</p>
    <table>
      ${line('Received from', r.giver.name)}
      ${line('Towards', r.towards)}
      ${line('Method', (r.method || '').replace(/_/g, ' '))}
      ${line('Reference', r.reference)}
      ${line('Date', (r.issued_on || '').slice(0, 10))}
    </table>
    <hr/>
    <table>
      ${line('Pledged', money(r.pledge.amount, r.pledge.currency))}
      ${line('Paid to date', money(r.pledge.paid, r.pledge.currency))}
      ${line('Outstanding when issued', money(r.pledge.outstanding, r.pledge.currency))}
    </table>
    <p class="muted" style="margin-top:24px">Thank you. Received with gratitude.</p>
    </body></html>`);
  win.document.close();
  win.focus();
  win.print();
};

export const ReceiptDialog = ({ churchId, pledgeId, paymentId, onClose }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['receipt', pledgeId, paymentId],
    queryFn: () => api.get(
      `/church/${churchId}/pledges/${pledgeId}/payments/${paymentId}/receipt`)
      .then((r) => r.data.receipt),
    enabled: !!paymentId,
  });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="receipt-dialog">
        <DialogHeader><DialogTitle className="text-base">Receipt</DialogTitle></DialogHeader>
        {isLoading || !data ? (
          <p className="flex items-center gap-2 p-4 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />Preparing the receipt…
          </p>
        ) : (
          <div className="space-y-3 font-serif">
            <div className="flex items-center gap-3">
              {data.assembly.logo_url ? (
                <img src={data.assembly.logo_url} alt={data.assembly.name}
                     className="h-12 w-auto max-w-[140px] object-contain"
                     data-testid="receipt-logo" />
              ) : null}
              <div>
                <p className="text-base font-semibold text-slate-900">{data.assembly.name}</p>
                <p className="text-xs text-slate-500">{data.assembly.address}</p>
              </div>
            </div>
            {data.reversed ? (
              <p className="inline-block -rotate-2 border-2 border-rose-700 px-2 py-0.5
                            text-xs font-bold tracking-widest text-rose-700"
                 data-testid="receipt-reversed">REVERSED</p>
            ) : null}
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="text-xs uppercase tracking-wide text-slate-400">Receipt number</p>
              <p className="font-mono text-lg tracking-wider text-slate-900"
                 data-testid="receipt-number">{data.receipt_number || '—'}</p>
              <p className="mt-2 text-2xl font-bold text-slate-900" data-testid="receipt-amount">
                {money(data.amount, data.currency)}
              </p>
            </div>
            <dl className="grid grid-cols-[8rem_1fr] gap-y-1 text-sm">
              <dt className="text-slate-500">Received from</dt><dd>{data.giver.name}</dd>
              <dt className="text-slate-500">Towards</dt><dd>{data.towards}</dd>
              <dt className="text-slate-500">Method</dt>
              <dd>{(data.method || '').replace(/_/g, ' ')}</dd>
              {data.reference ? (
                <>
                  <dt className="text-slate-500">Reference</dt><dd>{data.reference}</dd>
                </>
              ) : null}
              <dt className="text-slate-500">Date</dt><dd>{(data.issued_on || '').slice(0, 10)}</dd>
              <dt className="text-slate-500">Outstanding when issued</dt>
              <dd>{money(data.pledge.outstanding, data.pledge.currency)}</dd>
            </dl>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={() => printReceipt(data)} disabled={!data}
                  data-testid="receipt-print">
            <Printer className="mr-1.5 h-4 w-4" />Print
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReceiptDialog;
